'use client'

import { useState } from 'react'

import { Avatar, Icon, IconComponent } from '@graftcode/design-system'
import * as Dialog from '@radix-ui/react-dialog'

import { AUTH_LOGOUT_PATH } from '../../../../const/auth-logout-path.const'
import { useSessionStore } from '../../../../stores'

import styles from '../../MobileMenu.module.scss'

interface AccountDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback to close the drawer */
  onClose: () => void
  /** User display name */
  userName: string
  /** User email */
  userEmail: string
  /** User avatar URL */
  userAvatarUrl?: string
  /** Callback when an action is clicked */
  onActionClick: (actionId: string) => void
}

export function AccountDrawer({
  isOpen,
  onClose,
  userName,
  userEmail,
  userAvatarUrl,
  onActionClick,
}: AccountDrawerProps) {
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleActionClick = (actionId: string, disabled?: boolean) => {
    if (disabled) return
    onActionClick(actionId)
    onClose()
  }

  const handleLogout = () => {
    setIsLoggingOut(true)
    useSessionStore.getState().clearToken()
    window.location.href = AUTH_LOGOUT_PATH
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && !isLoggingOut && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.appPickerOverlay} />
        <Dialog.Content className={styles.accountDrawer}>
          {/* Header */}
          <div className={styles.accountDrawerHeader}>
            <div className={styles.appPickerDragHandle} />
            <Dialog.Title className={styles.accountDrawerTitleSection}>
              <Avatar size={48} src={userAvatarUrl} />
              <div className={styles.accountDrawerTitleText}>
                <span className={styles.accountDrawerName}>{userName}</span>
                <span className={styles.accountDrawerEmail}>{userEmail}</span>
              </div>
            </Dialog.Title>
            <div className={styles.appPickerDivider} />
          </div>

          {/* Actions List */}
          <div className={styles.accountDrawerContent}>
            {/* Settings */}
            <button className={styles.accountDrawerItem} onClick={() => handleActionClick('settings')}>
              <IconComponent icon={Icon.SettingsRegular} size={24} className={styles.accountDrawerItemIcon} />
              <span className={styles.accountDrawerItemLabel}>Settings</span>
            </button>

            {/* Region */}
            <button
              className={`${styles.accountDrawerItem} ${styles.disabled}`}
              onClick={() => handleActionClick('region', true)}
              disabled
            >
              <IconComponent icon={Icon.EarthRegular} size={24} className={styles.accountDrawerItemIcon} />
              <span className={styles.accountDrawerItemLabel}>Region</span>
            </button>

            {/* Separator */}
            <div className={styles.accountDrawerSeparator} />

            {/* Logout */}
            <button
              className={`${styles.accountDrawerItem} ${isLoggingOut ? styles.loading : ''}`}
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <IconComponent
                icon={isLoggingOut ? Icon.DSSpinner : Icon.ArrowExitRegular}
                size={24}
                className={`${styles.accountDrawerItemIcon} ${isLoggingOut ? styles.spinning : ''}`}
              />
              <span className={styles.accountDrawerItemLabel}>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
